/**
 * src/firebase/hierarquia.js
 *
 * Client-side da hierarquia entre escolas: gerar/revogar convite,
 * aceitar convite, e consultar os relatórios recebidos das escolas
 * vinculadas (como Regional e/ou como Geral).
 *
 * Ver backend/hierarquia.js para as Cloud Functions correspondentes e
 * o porquê de regionalId/geralId nunca serem escritos direto pelo client.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";

export async function gerarConvite(idEscola, tipoAlvo) {
  const functions = getFunctions();
  const fn = httpsCallable(functions, "gerarConvite");
  const resultado = await fn({ idEscola, tipoAlvo });
  return resultado.data; // { token, tipoAlvo }
}

export async function revogarConvite(idEscola, token) {
  const functions = getFunctions();
  const fn = httpsCallable(functions, "revogarConvite");
  const resultado = await fn({ idEscola, token });
  return resultado.data; // { success }
}

export async function aceitarConvite(idEscola, token) {
  const functions = getFunctions();
  const fn = httpsCallable(functions, "aceitarConvite");
  const resultado = await fn({ idEscola, token });
  return resultado.data; // { success, tipoAlvo, nomeEscolaPai }
}

/**
 * Convites ativos da própria escola (pra mostrar o link já gerado, sem
 * precisar chamar gerarConvite de novo a cada vez que a tela abre).
 */
export async function buscarConvitesAtivos(idEscola) {
  const snap = await getDocs(
    query(collection(db, "escolas", idEscola, "convites"), where("ativo", "==", true)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Escolas já vinculadas a esta (como Regional e/ou como Geral dela).
 */
export async function buscarEscolasVinculadas(idEscola) {
  const snap = await getDocs(collection(db, "escolas", idEscola, "vinculos"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Info de hierarquia da PRÓPRIA escola: se ela está vinculada a uma
 * Regional/Geral, e o nome de cada uma (pra exibir na tela).
 */
export async function buscarMinhaHierarquia(idEscola) {
  const escolaSnap = await getDoc(doc(db, "escolas", idEscola));
  if (!escolaSnap.exists()) return { regionalId: null, geralId: null };

  const { regionalId, geralId } = escolaSnap.data();
  const [regionalSnap, geralSnap] = await Promise.all([
    regionalId ? getDoc(doc(db, "escolas", regionalId)) : Promise.resolve(null),
    geralId ? getDoc(doc(db, "escolas", geralId)) : Promise.resolve(null),
  ]);

  return {
    regionalId: regionalId || null,
    nomeRegional: regionalSnap?.exists() ? regionalSnap.data().nome : null,
    geralId: geralId || null,
    nomeGeral: geralSnap?.exists() ? geralSnap.data().nome : null,
  };
}

/**
 * Relatórios mensais recebidos das escolas vinculadas — via
 * collectionGroup, sem precisar "descer" a árvore escola por escola.
 * `papel` é "regional" ou "geral" (qual campo denormalizado usar como
 * filtro: regionalId ou geralId). Requer o índice de collectionGroup
 * correspondente em firestore.indexes.json.
 */
export async function buscarRelatoriosRecebidos(idEscola, papel) {
  const campo = papel === "geral" ? "geralId" : "regionalId";
  const snap = await getDocs(
    query(
      collectionGroup(db, "relatorio"),
      where(campo, "==", idEscola),
      orderBy("data.ano", "desc"),
      orderBy("data.mes", "desc"),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
